import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import AdvancedFilter from "../../component/AdvancedFilter";
import * as Yup from 'yup';
import {Grid, TextField} from "@mui/material";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {useAffiliateActions} from "../../service/useAffiliateActions";
import AffiliateDetailModal from "./modals/AffiliateDetailModal";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {Stack} from "@mui/system";
import EmptyState from "../../../../util/component/EmptyState";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";

const AffiliateListPage = () => {
    const {t} = useTranslation(['salesmarketing', 'translation']);
    const affiliateActions = useAffiliateActions();
    const {globalData} = useContext(GlobalContext);
    const [searchInput, setSearchInput] = useState("");
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [affiliateIdDetail, setAffiliateIdDetail] = useState("");
    const gridRef = useRef<AgGridReact>(null);
    const [filters, setFilters] = useState<any>({
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        companyName: '',
        vatNumber: '',
        siren: '',
        idSAP: '',
        vendorCode: ''
    });

    const defaultFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        companyName: '',
        vatNumber: '',
        siren:'',
        idSAP: '',
        vendorCode: ''
    }

    const validationObj = {
        companyName: Yup.string().nullable(),
        vatNumber: Yup.string().nullable(),
        siren: Yup.string().nullable(),
        idSAP: Yup.string().nullable(),
        vendorCode: Yup.string().nullable()
    }

    const fields = {
        companyName: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:affiliate.filters.companyNameField')}
                inputProps={{maxLength: 120}}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        vatNumber: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:affiliate.filters.vatNumberField')}
                inputProps={{maxLength: 11}}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        siren: (field: any) => (
                <TextField
                    fullWidth
                    label={t('salesmarketing:affiliate.filters.sirenField')}
                    inputProps={{maxLength: 11}}
                    value={field.value}
                    onChange={field.onChange}
                />
            ),
        idSAP: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:affiliate.filters.idSAPField')}
                inputProps={{maxLength: 30}}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        vendorCode: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:affiliate.filters.vendorCodeField')}
                inputProps={{maxLength: 30}}
                value={field.value}
                onChange={field.onChange}
            />
        )
    }

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('salesmarketing:legalEntityField'),
            tooltipField: "tenantId",
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true
        },
        {
            field: "dealerId",
            headerName: t('salesmarketing:dealerField'),
            tooltipField: "dealerId",
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true
        },
        {
            field: "repCompanyName",
            headerName: t('salesmarketing:affiliate.listColumns.companyNameHeader'),
            tooltipField: "companyName",
            unSortIcon: true
        },
        {
            field: "repVatNumber",
            headerName: t('salesmarketing:affiliate.listColumns.vatNumberHeader'),
            tooltipField: "vatNumber",
            unSortIcon: true
        },
        {
                field: "siren",
                headerName: t('salesmarketing:affiliate.listColumns.sirenHeader'),
                tooltipField: "siren",
                unSortIcon: true
        },
        {
                field: "siret",
                headerName: t('salesmarketing:affiliate.listColumns.siretHeader'),
                tooltipField: "siret",
                unSortIcon: true
        },
        {
            field: "genBpCode",
            headerName: t('salesmarketing:affiliate.listColumns.idSAPHeader'),
            tooltipField: "genBpCode",
            unSortIcon: true
        },
        {
            field: "genVendorCode",
            headerName: t('salesmarketing:affiliate.listColumns.vendorCodeHeader'),
            tooltipField: "genVendorCode",
            unSortIcon: true
        },
        {
            field: "_actions",
            maxWidth: 160,
            headerName: t('salesmarketing:affiliate.listColumns.actionHeader'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            window.open("/salesmarketing/affiliateupdate/" + params.data.genBpCode, "_blank");
                        }} auth={'SCOPE_SM_UPDATE_AFFILIATES'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setAffiliateIdDetail(params.data.genBpCode);
                            setOpenDetailModal(true);
                        }} auth={'SCOPE_SM_VIEW_AFFILIATES_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    const createDatasource = (filters: any, api :GridApi) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filtersTemp = Object.assign({}, filters);
                filtersTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filtersTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filtersTemp.offset;
                filtersTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filtersTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filtersTemp.search = searchInput;
                let data = await affiliateActions.listAffiliates(filtersTemp);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        }
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['genBpCode'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns,
        noRowsOverlayComponentParams: {title: t('salesmarketing:affiliate.noRows')}
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    return (
        <>
            <SectionHeader title={t('salesmarketing:affiliate.listHeader.title')}
                           subtitle={t('salesmarketing:affiliate.listHeader.subTitle')}/>
            <AdvancedFilter validationObj={validationObj} defaultValues={defaultFilters} fields={fields}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef}
                                       noRowsOverlayComponent={noRowsOverlayComponent}
                                       onGridReady={(params) => {
                                           params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                                       }}/>
                </Grid>
            </Grid>
            <AffiliateDetailModal open={openDetailModal} setOpen={setOpenDetailModal} affiliateId={affiliateIdDetail}/>
        </>
    );
}

export default AffiliateListPage;